import { useState, useCallback, useEffect } from 'react';
import { numbersApi, messagesApi, voiceApi } from '@/services/api';
import { useRealtime } from '@/providers/realtime-provider';
import type { NumberPool, Assignment, SmsMessage, VoiceEvent } from '@/types';

interface UseNumbersReturn {
  numbers: NumberPool[];
  assignments: Assignment[];
  messages: SmsMessage[];
  voiceEvents: VoiceEvent[];
  loading: boolean;
  error: Error | null;
  fetchNumbers: (params?: { page?: number; limit?: number; country?: string; status?: string }) => Promise<void>;
  fetchAssignments: (params?: { page?: number; limit?: number; status?: string }) => Promise<void>;
  fetchMessages: (params?: { page?: number; limit?: number; assignmentId?: string }) => Promise<void>;
  fetchVoiceEvents: (params?: { page?: number; limit?: number; assignmentId?: string }) => Promise<void>;
  assignNumber: (data: { country: string; type?: string; providerId?: string }) => Promise<Assignment | null>;
  releaseNumber: (assignmentId: string) => Promise<boolean>;
  sendSms: (data: { to: string; from: string; body: string }) => Promise<SmsMessage | null>;
  makeCall: (data: { to: string; from: string; webhookUrl?: string }) => Promise<VoiceEvent | null>;
  refetch: () => void;
}

export function useNumbers(): UseNumbersReturn {
  const [numbers, setNumbers] = useState<NumberPool[]>([]);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [messages, setMessages] = useState<SmsMessage[]>([]);
  const [voiceEvents, setVoiceEvents] = useState<VoiceEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const { events } = useRealtime();

  const fetchNumbers = useCallback(async (params?: { page?: number; limit?: number; country?: string; status?: string }) => {
    setLoading(true);
    setError(null);
    try {
      const response = await numbersApi.getNumbers(params);
      if (response?.success && response.data) {
        const items = Array.isArray(response.data) ? response.data : [response.data];
        setNumbers(items.map((n: any) => ({
          id: n.id,
          providerId: n.provider_id || '',
          phoneNumber: n.e164 || n.phone_number || '',
          countryCode: n.country_code || '',
          areaCode: n.area_code || null,
          numberType: n.number_type || 'mobile',
          capabilities: n.supports_sms ? ['sms'] : [],
          monthlyCost: n.monthly_cost_usd || 0,
          setupCost: n.acquisition_cost_usd || 0,
          status: n.status || 'available',
          assignedTo: n.assigned_user_id || null,
          assignedAt: n.assigned_at ? new Date(n.assigned_at).toISOString() : null,
          expiresAt: n.assignment_expires_at ? new Date(n.assignment_expires_at).toISOString() : null,
          metadata: {},
          createdAt: new Date(n.created_at || Date.now()).toISOString(),
          updatedAt: new Date(n.updated_at || Date.now()).toISOString(),
        })));
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch numbers'));
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAssignments = useCallback(async (params?: { page?: number; limit?: number; status?: string }) => {
    setLoading(true);
    setError(null);
    try {
      const response = await numbersApi.getAssignments(params);
      if (response?.success && response.data) {
        const items = Array.isArray(response.data) ? response.data : [response.data];
        setAssignments(items.map((a: any) => ({
          id: a.id,
          userId: a.user_id || '',
          numberPoolId: a.number_id || '',
          phoneNumber: a.e164 || '',
          status: a.status || 'active',
          assignedAt: new Date(a.created_at || Date.now()).toISOString(),
          expiresAt: a.expires_at ? new Date(a.expires_at).toISOString() : null,
          renewedAt: a.renewed_at ? new Date(a.renewed_at).toISOString() : null,
          autoRenew: !!a.auto_renew,
          monthlyReward: a.monthly_reward_usd || 0,
          createdAt: new Date(a.created_at || Date.now()).toISOString(),
          updatedAt: new Date(a.updated_at || Date.now()).toISOString(),
        })));
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch assignments'));
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMessages = useCallback(async (params?: { page?: number; limit?: number; assignmentId?: string }) => {
    setLoading(true);
    setError(null);
    try {
      const response = await messagesApi.getMessages(params);
      if (response?.success && response.data) {
        const items = Array.isArray(response.data) ? response.data : [response.data];
        setMessages(items.map((m: any) => ({
          id: m.id,
          assignmentId: m.assignment_id || '',
          userId: m.user_id || '',
          providerId: m.provider_id || '',
          phoneNumber: m.e164 || '',
          direction: m.direction || 'inbound',
          fromNumber: m.from_number || '',
          toNumber: m.to_number || '',
          body: m.body || '',
          segments: m.segments || 1,
          encoding: m.encoding || 'GSM-7',
          status: m.status || 'received',
          providerMessageId: m.provider_message_id || null,
          errorCode: m.error_code || null,
          errorMessage: m.error_message || null,
          rewardAmount: m.reward_usd || 0,
          rewardStatus: m.reward_status || 'pending',
          processedAt: m.processed_at ? new Date(m.processed_at).toISOString() : null,
          createdAt: new Date(m.created_at || Date.now()).toISOString(),
          updatedAt: new Date(m.updated_at || Date.now()).toISOString(),
        })));
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch messages'));
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchVoiceEvents = useCallback(async (params?: { page?: number; limit?: number; assignmentId?: string }) => {
    setLoading(true);
    setError(null);
    try {
      const response = await voiceApi.getEvents(params);
      if (response?.success && response.data) {
        const items = Array.isArray(response.data) ? response.data : [response.data];
        setVoiceEvents(items.map((v: any) => ({
          id: v.id,
          assignmentId: v.assignment_id || '',
          userId: v.user_id || '',
          providerId: v.provider_id || '',
          phoneNumber: v.e164 || '',
          direction: v.direction || 'inbound',
          fromNumber: v.from_number || '',
          toNumber: v.to_number || '',
          callSid: v.call_sid || null,
          status: v.status || 'completed',
          durationSeconds: v.duration_seconds || 0,
          recordingUrl: v.recording_url || null,
          transcription: v.transcription || null,
          transcriptionStatus: v.transcription_status || null,
          rewardAmount: v.reward_usd || 0,
          rewardStatus: v.reward_status || 'pending',
          startedAt: v.started_at ? new Date(v.started_at).toISOString() : null,
          answeredAt: v.answered_at ? new Date(v.answered_at).toISOString() : null,
          endedAt: v.ended_at ? new Date(v.ended_at).toISOString() : null,
          createdAt: new Date(v.created_at || Date.now()).toISOString(),
          updatedAt: new Date(v.updated_at || Date.now()).toISOString(),
        })));
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch voice events'));
    } finally {
      setLoading(false);
    }
  }, []);

  const assignNumber = useCallback(async (data: { country: string; type?: string; providerId?: string }) => {
    setLoading(true);
    setError(null);
    try {
      const response = await numbersApi.assignNumber(data);
      if (response?.success && response.data) {
        const a = response.data;
        const assignment: Assignment = {
          id: a.assignment_id || a.id || '',
          userId: '',
          numberPoolId: '',
          phoneNumber: a.phone_number || '',
          status: 'active',
          assignedAt: new Date().toISOString(),
          expiresAt: a.expires_in ? new Date(Date.now() + a.expires_in * 1000).toISOString() : null,
          renewedAt: null,
          autoRenew: false,
          monthlyReward: 0,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        setAssignments(prev => [assignment, ...prev]);
        return assignment;
      }
      throw new Error('Failed to assign number');
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to assign number'));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const releaseNumber = useCallback(async (assignmentId: string) => {
    setLoading(true);
    setError(null);
    try {
      await numbersApi.releaseNumber(assignmentId);
      setAssignments(prev => prev.map(a => a.id === assignmentId ? { ...a, status: 'revoked' as const, renewedAt: new Date().toISOString() } : a));
      return true;
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to release number'));
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const sendSms = useCallback(async (data: { to: string; from: string; body: string }) => {
    setLoading(true);
    setError(null);
    try {
      const response = await messagesApi.sendSms(data);
      if (response?.success && response.data) {
        const m = response.data;
        const message: SmsMessage = {
          id: m.id || '',
          assignmentId: m.assignment_id || '',
          userId: m.user_id || '',
          providerId: m.provider_id || '',
          phoneNumber: m.e164 || '',
          direction: 'outbound',
          fromNumber: m.from_number || data.from,
          toNumber: m.to_number || data.to,
          body: m.body || data.body,
          segments: m.segments || 1,
          encoding: 'GSM-7',
          status: m.status || 'sent',
          providerMessageId: m.provider_message_id || null,
          errorCode: null,
          errorMessage: null,
          rewardAmount: m.reward_usd || 0,
          rewardStatus: m.reward_status || 'pending',
          processedAt: m.processed_at ? new Date(m.processed_at).toISOString() : null,
          createdAt: new Date(m.created_at || Date.now()).toISOString(),
          updatedAt: new Date(m.updated_at || Date.now()).toISOString(),
        };
        setMessages(prev => [message, ...prev]);
        return message;
      }
      throw new Error('Failed to send SMS');
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to send SMS'));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const makeCall = useCallback(async (data: { to: string; from: string; webhookUrl?: string }) => {
    setLoading(true);
    setError(null);
    try {
      const response = await voiceApi.makeCall(data);
      if (response?.success && response.data) {
        const v = response.data;
        const event: VoiceEvent = {
          id: v.id || '',
          assignmentId: v.assignment_id || '',
          userId: v.user_id || '',
          providerId: v.provider_id || '',
          phoneNumber: v.e164 || '',
          direction: 'outbound',
          fromNumber: v.from_number || data.from,
          toNumber: v.to_number || data.to,
          callSid: v.call_sid || null,
          status: 'initiated',
          durationSeconds: 0,
          recordingUrl: null,
          transcription: null,
          transcriptionStatus: null,
          rewardAmount: v.reward_usd || 0,
          rewardStatus: 'pending',
          startedAt: new Date().toISOString(),
          answeredAt: null,
          endedAt: null,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        setVoiceEvents(prev => [event, ...prev]);
        return event;
      }
      throw new Error('Failed to make call');
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to make call'));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Handle realtime events
  useEffect(() => {
    events.forEach(event => {
      if (event.type === 'sms_received' && event.payload) {
        setMessages(prev => [event.payload as unknown as SmsMessage, ...prev]);
      }
      if (event.type === 'voice_event' && event.payload) {
        setVoiceEvents(prev => [event.payload as unknown as VoiceEvent, ...prev]);
      }
      if (event.type === 'assignment_changed' && event.payload) {
        setAssignments(prev => prev.map(a => a.id === event.payload?.id ? { ...a, ...event.payload } : a));
      }
    });
  }, [events]);

  const refetch = useCallback(() => {
    fetchNumbers();
    fetchAssignments();
    fetchMessages();
    fetchVoiceEvents();
  }, [fetchNumbers, fetchAssignments, fetchMessages, fetchVoiceEvents]);

  return {
    numbers,
    assignments,
    messages,
    voiceEvents,
    loading,
    error,
    fetchNumbers,
    fetchAssignments,
    fetchMessages,
    fetchVoiceEvents,
    assignNumber,
    releaseNumber,
    sendSms,
    makeCall,
    refetch,
  };
}
